// ChromeContext — shared open/close state for the global chrome
// surfaces that the keyboard shortcuts need to drive: the Ask Stew
// panel (replaces the old GlobalSearch overlay as of Ask Stew PR #3),
// the keyboard cheat sheet, and the sidebar expand/collapse.
//
// Each surface (StewChatPanel, KeyboardCheatSheet, Sidebar) consumes
// this via useChrome() so that keyboard handlers + the user menu can
// open / close them without prop drilling.
//
// The provider lives inside PageLayout, alongside the toast provider,
// so it's only mounted for authenticated routes — chrome state on
// /sign-in / /accept-invite would be meaningless.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";

import { useUserSetting, useUserSettingsLoaded } from "../settings/user-settings-context";
import { NAV_LAST_SECTION, NAV_SECOND_PANE_MODE } from "../settings/user-settings-registry";

// Per-device UX state (panel size preference). Lives in localStorage
// per the v1.28 doctrine — fine to lose on cache clear; not a cross-
// device preference.
const STEW_EXPANDED_LS_KEY = "stewardshipos.askstew.expanded";

export interface ChromeApi {
  // ── Left navigation (two-pane as of 2026-08-23) ─────────────────────
  //
  // The rail is a fixed 56px column of heading icons that is
  // ALWAYS visible on desktop; `activeNavSection` says which heading's
  // pane is open beside it, or null for "just the icons".
  //
  // There is deliberately no separate "is the rail collapsed" flag any
  // more. That was the second of two states that had to agree, and they
  // regularly didn't — a rail could be collapsed to icons while its
  // sections claimed to be open. One value now describes the whole
  // thing, and `isSidebarCollapsed` is kept purely as a derived alias so
  // existing callers (⌘/ and the layout's width rule) keep working.
  activeNavSection: string | null;
  closeNavSection: () => void;
  /** Open the section, or close the pane if it is already showing. */
  toggleNavSection: (key: string) => void;
  /** True when no section pane is open — i.e. the rail is icons only. */
  isSidebarCollapsed: boolean;
  /** ⌘/ — closes the open pane, or reopens the last one used. */
  toggleSidebar: () => void;
  // Mobile nav drawer (off-canvas sidebar on phones). Ephemeral — not
  // persisted; always starts closed on a fresh load.
  isMobileNavOpen: boolean;
  openMobileNav: () => void;
  closeMobileNav: () => void;
  // Ask Stew panel (replaces the GlobalSearch overlay — see Ask Stew
  // V1 plan §"PR #3 — Chat panel shell + ⌘K rebinding")
  isAskStewOpen: boolean;
  openAskStew: () => void;
  closeAskStew: () => void;
  // S2 PR 4 — type-through from search-box-shaped entry points (the
  // cockpit hero): open the palette with the user's first keystroke
  // already in the input so no typed character is lost. The panel
  // consumes (and clears) the seed on its open transition.
  askStewSeedDraft: string | null;
  openAskStewWithDraft: (seed: string) => void;
  clearAskStewSeedDraft: () => void;
  isAskStewExpanded: boolean;
  toggleAskStewExpanded: () => void;
  setAskStewExpanded: (expanded: boolean) => void;
  // Keyboard cheat sheet overlay
  isCheatSheetOpen: boolean;
  openCheatSheet: () => void;
  closeCheatSheet: () => void;
}

const ChromeContext = createContext<ChromeApi | null>(null);

// Below this width there is no room for the second pane beside the page
// content, so it force-closes to the icon rail. (The mobile drawer takes
// over entirely further down, at 768px — see styles.css.)
//
// This is a HARD constraint, not a preference: the "open on my last
// section" setting is honored on a full-size window and quietly
// overridden on a narrow one, because a pane that squeezes the page to
// nothing is worse than one that is shut.
const AUTO_COLLAPSE_MQ = "(max-width: 1100px)";

function viewportForcesClosed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.matchMedia?.(AUTO_COLLAPSE_MQ)?.matches ?? false;
  } catch {
    return false;
  }
}

function readInitialStewExpanded(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage?.getItem(STEW_EXPANDED_LS_KEY);
    return raw === "1";
  } catch {
    return false;
  }
}

export function ChromeProvider({ children }: { children: ReactNode }) {
  // Which heading's pane is open. Starts closed for EVERY session and is
  // opened afterwards by the effect below if the user's setting says so
  // — never the other way round. Rendering open-then-closed would flash
  // a 220px pane and shove the page sideways on every load for the
  // majority who keep the default.
  const [activeNavSection, setActiveNavSection] = useState<string | null>(null);
  const [secondPaneMode] = useUserSetting(NAV_SECOND_PANE_MODE);
  const [lastSection, setLastSection] = useUserSetting(NAV_LAST_SECTION);
  const settingsLoaded = useUserSettingsLoaded();
  const [isAskStewOpen, setAskStewOpen] = useState(false);
  const [isAskStewExpanded, setAskStewExpandedState] = useState<boolean>(() =>
    readInitialStewExpanded(),
  );
  const [isCheatSheetOpen, setCheatSheetOpen] = useState(false);
  // Mobile nav drawer — ephemeral session state, intentionally NOT
  // persisted to localStorage (always closed on a fresh load).
  const [isMobileNavOpen, setMobileNavOpen] = useState(false);

  const closeNavSection = useCallback(() => {
    setActiveNavSection(null);
  }, []);

  // Remember the section for "open on my last section", but only on a
  // real user action — the restore effect below must not write back what
  // it just read, or a user who never touches the rail slowly pins
  // themselves to whatever they opened once, months ago.
  const toggleNavSection = useCallback(
    (key: string) => {
      setActiveNavSection((prev) => (prev === key ? null : key));
      setLastSection(key);
    },
    [setLastSection],
  );

  const isSidebarCollapsed = activeNavSection === null;

  // ⌘/ — shut the open pane, or bring back the last one you had open.
  //
  // The fallback is "me" and that matters: every role has that section,
  // so this can never resolve to a key the user cannot see. The stored
  // `lastSection` CAN be such a key (a role change, or a value saved when
  // the user had access they no longer have) — Sidebar reconciles that,
  // see the note on its `openSection`.
  const toggleSidebar = useCallback(() => {
    setActiveNavSection((prev) => (prev === null ? (lastSection ?? "me") : null));
  }, [lastSection]);

  const openAskStew = useCallback(() => setAskStewOpen(true), []);
  const closeAskStew = useCallback(() => setAskStewOpen(false), []);

  const [askStewSeedDraft, setAskStewSeedDraft] = useState<string | null>(null);
  const openAskStewWithDraft = useCallback((seed: string) => {
    setAskStewSeedDraft(seed);
    setAskStewOpen(true);
  }, []);
  const clearAskStewSeedDraft = useCallback(() => setAskStewSeedDraft(null), []);

  const setAskStewExpanded = useCallback((expanded: boolean) => {
    setAskStewExpandedState(expanded);
  }, []);
  const toggleAskStewExpanded = useCallback(() => {
    setAskStewExpandedState((prev) => !prev);
  }, []);

  const openCheatSheet = useCallback(() => setCheatSheetOpen(true), []);
  const closeCheatSheet = useCallback(() => setCheatSheetOpen(false), []);

  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  // Restore "open on my last section", once, after the account settings
  // arrive. Guarded by a ref rather than a dependency list because the
  // settings blob can legitimately change later (another tab saves a
  // preference) and reopening the pane under someone mid-task would be
  // the app moving on its own.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || !settingsLoaded) return;
    restoredRef.current = true;
    if (secondPaneMode !== "last") return;
    if (!lastSection) return;
    // A narrow window has no room for the pane; the setting waits for a
    // wider one rather than squeezing the page.
    if (viewportForcesClosed()) return;
    setActiveNavSection(lastSection);
  }, [settingsLoaded, secondPaneMode, lastSection]);

  // Force the pane shut when the window gets too narrow for it. Only the
  // narrowing direction is automatic: widening the window does NOT
  // reopen a pane the user closed, because the system re-opening a
  // surface you deliberately shut is the behavior Nate complained about
  // in the old collapse logic (2026-07-23).
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(AUTO_COLLAPSE_MQ);
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setActiveNavSection(null);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Persist Ask Stew expanded preference. Same per-device-only
  // posture as the sidebar.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage?.setItem(STEW_EXPANDED_LS_KEY, isAskStewExpanded ? "1" : "0");
    } catch {
      // Storage unavailable — silently no-op.
    }
  }, [isAskStewExpanded]);

  const api = useMemo<ChromeApi>(
    () => ({
      activeNavSection,
      closeNavSection,
      toggleNavSection,
      isSidebarCollapsed,
      toggleSidebar,
      isAskStewOpen,
      openAskStew,
      closeAskStew,
      askStewSeedDraft,
      openAskStewWithDraft,
      clearAskStewSeedDraft,
      isAskStewExpanded,
      toggleAskStewExpanded,
      setAskStewExpanded,
      isCheatSheetOpen,
      openCheatSheet,
      closeCheatSheet,
      isMobileNavOpen,
      openMobileNav,
      closeMobileNav,
    }),
    [
      activeNavSection,
      closeNavSection,
      toggleNavSection,
      isSidebarCollapsed,
      toggleSidebar,
      isAskStewOpen,
      openAskStew,
      closeAskStew,
      askStewSeedDraft,
      openAskStewWithDraft,
      clearAskStewSeedDraft,
      isAskStewExpanded,
      toggleAskStewExpanded,
      setAskStewExpanded,
      isCheatSheetOpen,
      openCheatSheet,
      closeCheatSheet,
      isMobileNavOpen,
      openMobileNav,
      closeMobileNav,
    ],
  );

  return <ChromeContext.Provider value={api}>{children}</ChromeContext.Provider>;
}

export function useChrome(): ChromeApi {
  const ctx = useContext(ChromeContext);
  if (!ctx) {
    throw new Error("useChrome must be called inside a <ChromeProvider> tree.");
  }
  return ctx;
}
